import { useState, useEffect } from 'react';
import { pricingAPI } from '../api/pricing';
import { Check, HelpCircle, ArrowRight, Filter, Sparkles } from 'lucide-react';
import Loader from '../components/common/Loader';
import { useNavigate } from 'react-router-dom';

export default function Pricing() {
  const [pricingPlans, setPricingPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    loadPricing();
  }, []);

  const loadPricing = async () => {
    try {
      setLoading(true);
      const response = await pricingAPI.getAll();
      console.log('Réponse API pricing:', response);
      
      const data = Array.isArray(response?.data) ? response.data : 
                   Array.isArray(response) ? response : [];
      
      console.log('Données pricing:', data);
      
      setPricingPlans(data);
      
      const uniqueCategories = [...new Set(data.map(item => item.type))];
      setCategories(uniqueCategories);
    } catch (error) {
      console.error('Erreur lors du chargement des tarifs:', error);
      setPricingPlans([]);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChoosePlan = (plan, category, planTitle) => {
    console.log('Plan sélectionné:', { plan, category, planTitle });
    navigate('/devis', {
      state: {
        selectedPlan: plan.name,
        selectedCategory: category,
        planTitle: planTitle,
        planPrice: plan.price,
        planPeriod: plan.period,
        planDescription: plan.description,
        planFeatures: plan.features || []
      }
    });
  };

  const filteredPlans = selectedCategory === 'all' 
    ? pricingPlans 
    : pricingPlans.filter(plan => plan.type === selectedCategory);

  const getCategoryLabel = (type) => {
    const labels = {
      'website': 'Sites Web',
      'ecommerce': 'E-commerce',
      'marketing': 'Marketing',
      'print': 'Impression',
      'maintenance': 'Maintenance',
      'hosting': 'Hébergement',
      'seo': 'SEO',
      'social': 'Réseaux Sociaux'
    };
    return labels[type] || type;
  };

  const getCategoryColor = (type) => {
    const colors = {
      'website': 'from-blue-500 to-cyan-500',
      'ecommerce': 'from-purple-500 to-pink-500',
      'marketing': 'from-green-500 to-emerald-500',
      'seo': 'from-amber-500 to-orange-500',
      'social': 'from-pink-500 to-rose-500',
      'hosting': 'from-indigo-500 to-blue-500',
      'maintenance': 'from-teal-500 to-cyan-500',
      'print': 'from-gray-500 to-gray-700'
    };
    return colors[type] || 'from-blue-500 to-purple-500';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-black to-gray-900 pt-20 flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-black to-gray-900 pt-20">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 via-indigo-900/10 to-purple-900/20" />
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-blue-500 to-transparent" />
        
        <div className="relative container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 mb-6 backdrop-blur-sm">
              <Sparkles className="w-4 h-4 text-blue-400" />
              <span className="text-sm font-semibold text-blue-300 uppercase">Tarifs Transparents</span>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Des Solutions à <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">Prix Justes</span>
            </h1>
            
            <p className="text-xl text-gray-300 max-w-2xl mx-auto leading-relaxed">
              Investissez dans votre succès digital avec nos forfaits optimisés pour maximiser votre ROI.
              Des tarifs clairs, sans surprises.
            </p>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="pb-8">
        <div className="container mx-auto px-4">
          {categories.length > 0 && (
            <div className="max-w-6xl mx-auto mb-12 p-6 bg-gray-800/30 backdrop-blur-sm rounded-2xl border border-gray-700">
              <div className="flex items-center gap-3 mb-6">
                <Filter className="w-5 h-5 text-blue-400" />
                <span className="text-lg font-semibold text-gray-300">Filtrer par catégorie:</span>
              </div>
              
              <div className="flex flex-wrap gap-3">
                <button 
                  onClick={() => setSelectedCategory('all')}
                  className={`px-5 py-3 rounded-xl transition-all duration-300 relative z-10 ${
                    selectedCategory === 'all'
                      ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg shadow-blue-500/25'
                      : 'bg-gray-800/50 text-gray-400 hover:text-white hover:bg-gray-700/50 border border-gray-700'
                  }`}
                >
                  Tous les services
                </button>
                {categories.map((category) => (
                  <button 
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`px-5 py-3 rounded-xl transition-all duration-300 relative z-10 ${
                      selectedCategory === category
                        ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg shadow-blue-500/25'
                        : 'bg-gray-800/50 text-gray-400 hover:text-white hover:bg-gray-700/50 border border-gray-700'
                    }`}
                  >
                    {getCategoryLabel(category)}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Pricing Plans */}
      <section className="pb-32">
        <div className="container mx-auto px-4">
          {filteredPlans.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 flex items-center justify-center">
                <HelpCircle className="w-12 h-12 text-gray-500" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">
                {pricingPlans.length === 0 ? 'Aucun tarif disponible' : 'Aucun tarif disponible pour cette catégorie'}
              </h3>
              <p className="text-gray-400 max-w-md mx-auto mb-8">
                Contactez-nous pour obtenir un devis personnalisé adapté à vos besoins.
              </p>
              <button 
                onClick={() => navigate('/devis')}
                className="inline-flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white font-bold rounded-xl hover:shadow-lg hover:shadow-blue-500/25 transition-all duration-200 hover:gap-3 relative z-10"
              >
                <span>Demander un devis</span>
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          ) : (
            <div className="space-y-16">
              {filteredPlans.map((plan) => (
                <div key={plan._id} className="group">
                  {/* Category Header */}
                  <div className="text-center mb-12">
                    <div className="inline-flex items-center gap-2 mb-4">
                      <span className={`px-4 py-2 bg-gradient-to-r ${getCategoryColor(plan.type)} text-white text-sm font-bold rounded-full`}>
                        {getCategoryLabel(plan.type)}
                      </span>
                    </div>
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">{plan.title}</h2>
                    <p className="text-gray-300 text-lg max-w-3xl mx-auto">{plan.description}</p>
                  </div>

                  {/* Plans Grid */}
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {plan.plans && plan.plans.map((p, idx) => (
                      <div key={idx} className="relative">
                        {/* Card Container */}
                        <div className="relative group/plan">
                          {/* Card Background */}
                          <div className="absolute inset-0 bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl border border-gray-700 group-hover/plan:border-blue-500/50 transition-colors duration-300" />
                          <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5 opacity-0 group-hover/plan:opacity-100 transition-opacity duration-300 rounded-2xl" />
                          
                          {/* Popular Badge */}
                          {p.popular && (
                            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-20">
                              <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm font-bold rounded-full shadow-lg">
                                <Sparkles className="w-4 h-4" />
                                <span>Recommandé</span>
                              </div>
                            </div>
                          )}

                          {/* Card Content */}
                          <div className="relative p-8 z-10">
                            {/* Plan Header */}
                            <div className="mb-8">
                              <h3 className="text-2xl font-bold text-white mb-4">{p.name}</h3>
                              <div className="mb-4">
                                <div className="flex items-baseline gap-2">
                                  <span className="text-5xl font-black text-white">{p.price}€</span>
                                  <span className="text-gray-400">
                                    {p.period === 'monthly' ? '/mois' : 
                                     p.period === 'yearly' ? '/an' : 
                                     ' forfait'}
                                  </span>
                                </div>
                                {p.description && (
                                  <p className="text-gray-400 mt-2 text-sm">{p.description}</p>
                                )}
                              </div>
                            </div>

                            {/* Features */}
                            <div className="mb-8">
                              <h4 className="text-lg font-semibold text-white mb-4">Ce qui est inclus :</h4>
                              {p.features && p.features.length > 0 ? (
                                <ul className="space-y-3">
                                  {p.features.map((feature, fIdx) => (
                                    <li key={fIdx} className="flex items-start gap-3">
                                      <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                                      <span className="text-gray-300">{feature}</span>
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                <p className="text-gray-400 italic">Contactez-nous pour les détails</p>
                              )}
                            </div>

                            {/* Footer */}
                            <div className="pt-6 border-t border-gray-800 relative z-30">
                              <button 
                                onClick={() => handleChoosePlan(p, plan.type, plan.title)}
                                className="flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-blue-500/25 transition-all duration-200 hover:gap-3 mb-4 w-full relative z-40"
                              >
                                <span>Choisir ce forfait</span>
                                <ArrowRight className="w-4 h-4" />
                              </button>
                              
                              {p.period === 'monthly' && (
                                <p className="text-center text-sm text-gray-400">
                                  Facturé mensuellement, sans engagement
                                </p>
                              )}
                              {p.period === 'yearly' && (
                                <p className="text-center text-sm text-gray-400">
                                  Économisez 15% par rapport au mensuel
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Glow Effect (EN DESSOUS du contenu) */}
                          <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500/20 to-purple-500/20 opacity-0 group-hover/plan:opacity-100 blur transition-opacity duration-300 rounded-2xl -z-10" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* FAQ Section */}
          <div className="mt-32">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Questions Fréquentes</h2>
              <p className="text-gray-300 text-lg max-w-2xl mx-auto">
                Réponses aux questions les plus courantes sur nos tarifs et services
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
              {[
                {
                  question: "Y a-t-il des frais cachés ?",
                  answer: "Non, tous nos tarifs sont 100% transparents. Le prix annoncé est le prix final, sans surprises. Vous savez exactement ce que vous payez."
                },
                {
                  question: "Puis-je changer de forfait ?",
                  answer: "Oui, vous pouvez évoluer vers un forfait supérieur ou inférieur à tout moment, sans frais supplémentaires. Nous adaptons votre solution à vos besoins."
                },
                {
                  question: "Quelle est la durée des contrats ?",
                  answer: "Pas d'engagement. Vous pouvez résilier quand vous voulez, nous ne retenons pas nos clients prisonniers. Flexibilité totale pour votre entreprise."
                },
                {
                  question: "Proposez-vous des devis personnalisés ?",
                  answer: "Absolument. Contactez-nous pour un projet sur mesure qui répond exactement à vos besoins spécifiques et à votre budget."
                }
              ].map((faq, idx) => (
                <div key={idx} className="group bg-gray-800/30 backdrop-blur-sm border border-gray-700 rounded-2xl p-8 transition-all duration-300 hover:border-blue-500/50 hover:bg-gray-800/50 relative z-10">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center flex-shrink-0">
                      <HelpCircle className="w-6 h-6 text-blue-400" />
                    </div>
                    <h3 className="text-xl font-bold text-white">{faq.question}</h3>
                  </div>
                  <p className="text-gray-400 leading-relaxed">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Custom Solution Section */}
      <section className="pb-32">
        <div className="container mx-auto px-4">
          <div className="relative max-w-6xl mx-auto">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-3xl blur-xl" />
            
            <div className="relative bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm border border-gray-700 rounded-3xl overflow-hidden z-10">
              <div className="p-12 text-center md:text-left md:flex items-center gap-12">
                <div className="mb-8 md:mb-0 md:flex-shrink-0">
                  <div className="w-20 h-20 mx-auto md:mx-0 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
                    <Sparkles className="w-10 h-10 text-white" />
                  </div>
                </div>
                
                <div className="flex-1">
                  <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                    Projet <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-400">Spécial</span> ?
                  </h2>
                  <p className="text-gray-300 text-lg mb-6 max-w-2xl">
                    Nous créons des solutions sur mesure pour les entreprises avec des besoins uniques. 
                    Parlons de votre projet et créons ensemble la solution parfaite pour vous.
                  </p>
                  
                  <div className="flex flex-wrap gap-4 mb-8">
                    {[
                      "Devis gratuit sous 48h",
                      "Expert dédié à votre projet",
                      "Garantie satisfaction 100%",
                      "Suivi personnalisé"
                    ].map((benefit, idx) => (
                      <div key={idx} className="flex items-center gap-2 bg-gray-800/50 text-gray-300 px-4 py-2 rounded-lg border border-gray-700">
                        <Check className="w-4 h-4 text-green-400" />
                        <span className="text-sm font-medium">{benefit}</span>
                      </div>
                    ))}
                  </div>
                  
                  <button 
                    onClick={() => navigate('/devis')}
                    className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold rounded-xl hover:shadow-lg hover:shadow-amber-500/25 transition-all duration-200 hover:gap-4 relative z-20"
                  >
                    <span>Demander un devis personnalisé</span>
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}